import { List } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { Option } from '../option';
import { useRetornoAds } from '@mais-retorno-front/components/retorno-ads/context';
import {
  TRACK_EVENTS,
  useTrackingEvents,
} from '@mais-retorno-front/hooks/tracking-events';
import { withHiddenPrime } from '@mais-retorno-front/components/retorno-ads';
import {
  RETORNO_ADS_EVENT_ACTION,
  RETORNO_ADS_TYPE,
} from '@mais-retorno-front/components/retorno-ads/gtm.constant';

interface IAssetInfo {
  slug: string;
  displayName: string;
  value: string | number;
}

interface IFormattedAsset {
  cnpj: number;
  slug: string;
  nicename: string;
  url: string;
  info: IAssetInfo[];
  identifier: string;
  type: string;
}

interface IResultListProps {
  assets: IFormattedAsset[];
  comparisonMode: boolean;
  onOpen?: (status: boolean) => void;
  handleCloseDialog: () => void;
  clearInput: () => void;
}

export const ResultList = ({
  assets,
  comparisonMode,
  onOpen,
  handleCloseDialog,
  clearInput,
}: IResultListProps) => {
  const listRef = useRef<HTMLUListElement>(null);
  const { featuredToSearch } = useRetornoAds();
  const { trackEvent } = useTrackingEvents();

  const handleKeyDown = (event: KeyboardEvent) => {
    const { key } = event;
    const firstElementFocused = listRef?.current?.firstChild as HTMLElement;

    let currentFocus =
      document.activeElement?.getAttribute('role') === 'result-item'
        ? (document.activeElement as HTMLElement)
        : null;

    switch (key) {
      case 'ArrowDown':
        if (currentFocus) {
          currentFocus = currentFocus.nextElementSibling as HTMLElement;
          currentFocus?.focus();
        } else {
          firstElementFocused?.focus();
        }
        break;

      case 'ArrowUp':
        if (currentFocus) {
          currentFocus = currentFocus.previousElementSibling as HTMLElement;
          currentFocus?.focus();
        } else {
          firstElementFocused?.focus();
        }
        break;

      case 'Enter':
        if (currentFocus) {
          (currentFocus.firstChild as HTMLElement).click();
          onOpen(false);
        }
        break;

      case 'Escape':
        onOpen(false);
        break;
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (!featuredToSearch) return;

    trackEvent(TRACK_EVENTS.RETORNO_ADS, {
      retorno_ad_id: featuredToSearch.identifier,
      retorno_ad_event_action: RETORNO_ADS_EVENT_ACTION.VIEW,
      retorno_ad_type: RETORNO_ADS_TYPE.SEARCH,
    });
  }, [featuredToSearch]);

  return (
    <List ref={listRef}>
      {featuredToSearch && (
        <HiddenRetornoAdsOnPrime
          isFeatured
          asset={featuredToSearch}
          infos={featuredToSearch.info}
          handleCloseDialog={handleCloseDialog}
          clearInput={clearInput}
          comparisonMode={false}
          linkProps={{ target: '_blank' }}
          className="min-h-0 sm:min-h-0"
        />
      )}
      {assets.map((asset) => (
        <Option
          asset={asset}
          key={asset.slug}
          infos={asset.info}
          handleCloseDialog={handleCloseDialog}
          clearInput={clearInput}
          comparisonMode={comparisonMode}
        />
      ))}
    </List>
  );
};

const HiddenRetornoAdsOnPrime = withHiddenPrime(Option);
